/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from "react";
import Swal from "sweetalert2";
import * as yup from "yup";
import { useForm, Controller } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { yupResolver } from "@hookform/resolvers/yup";
import { cleannerError, getInTechTel, validatePhone } from "../../../../../utils/Utils";
import { Color } from "../../../../../utils/theme";
import { useAppSelector, useAppDispatch } from "../../../../../redux/hook";
import { removeCart } from "../../../../../redux/slice/useCartSlice";
import { GoogleAdresseType } from "../../../../../utils/type";
import { AppBaseUrl } from "../../../../../utils/http";
import {
  useAddOrEditOrderMutation,
  useAddOrEditOrderNotUserMutation,
} from "../../../../../utils/api/order/order.api";
import { buildOrderPayloadFromService } from "./serviceOrderMapping";

export interface ServiceFormData {
  step: number;
  user?: string | number;
  vendeur?: string | number;
  nom_complet?: string;
  telephone?: string;
  email?: string;
  adresse?: string;
  age?: string;
  profession?: string;
  situation_matrimoniale?: string;
  sexe?: string;
  nationalite?: string;
  religion?: string;
  date_rendez_vous?: string;
  heure_rendez_vous?: string;
  autres_traitements?: string;
  motifs_consultation?: string[];
  type_consultation?: string;
  symptomes?: string[];
  motif_rendez_vous?: string;
  consentement?: boolean;
  produits?: any[];
  montant_total?: number;
}

function UseAddOrUpdateServiceForm(next: any, previous: any, panier?: any, total?: number) {
  const { user } = useAppSelector((s) => s?.user);
  const { cartItems } = useAppSelector((state) => state.cart);
  const [code, setCode] = useState<string>("");
  const [phone, setPhone] = useState<string | null>(null);
  const [submitLoading, setSubmitLoading] = useState(false);
  const dispatch = useAppDispatch();
  const [adresse, setAdresse] = useState("");

  // step 1 = Informations personnelles
  // step 2 = Informations médicales
  // step 3 = Consentement
  // step 4 = Confirmation (soumission finale)
  const validationSchema = yup.object().shape({
    step: yup.number().default(1),
    nom_complet: yup.string().when("step", {
      is: 1,
      then: () =>
        user
          ? yup.string().nullable().label("Le nom complet")
          : yup.string().required().label("Le nom complet"),
    }),
    telephone: yup.string().when("step", {
      is: 1,
      then: () => validatePhone(yup, code).required(),
    }),
    email: yup.string().when("step", {
      is: 1,
      then: () => yup.string().email().nullable().label("L'email"),
    }),
    adresse: yup.string().when("step", {
      is: 1,
      then: () => yup.string().nullable().label("L'adresse"),
    }),
    date_rendez_vous: yup.string().when("step", {
      is: 1,
      then: () => yup.string().required().label("La date du rendez-vous"),
    }),
    heure_rendez_vous: yup.string().when("step", {
      is: 1,
      then: () => yup.string().required().label("L'heure du rendez-vous"),
    }),
    type_consultation: yup.string().when("step", {
      is: 2,
      then: () => yup.string().required().label("Le type de consultation"),
    }),
    consentement: yup.boolean().when("step", {
      is: 3,
      then: () =>
        yup
          .boolean()
          .oneOf([true], "Vous devez accepter les conditions")
          .required(),
    }),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    clearErrors,
    watch,
    control,
  } = useForm<ServiceFormData>({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      step: 1,
      motifs_consultation: [],
      symptomes: [],
      consentement: false,
    } as any,
  });

  const navigate = useNavigate();

  const [addOrUpdateOrder, { isLoading }] = user
    ? useAddOrEditOrderMutation()
    : useAddOrEditOrderNotUserMutation();

  const handleChangePhone = (item, country) => {
    setCode(country?.countryCode);
    setPhone(item);
    if (parseInt(item) !== parseInt(country?.dialCode)) {
      setValue("telephone", "+" + item);
    } else {
      setValue("telephone", "");
    }
  };

  const onChangeAdresse = (val: GoogleAdresseType) => {
    if (val?.rue) {
      setValue("adresse", val?.rue);
      setAdresse(val?.rue);
    } else {
      setValue("adresse", val as any);
      setAdresse(val as any);
    }
  };

  useEffect(() => {
    cleannerError(errors, clearErrors);
  }, [clearErrors, errors]);

  useEffect(() => {
    setPhone(user?.telephone);
  }, [user]);

  useEffect(() => {
    if (user?.id) {
      setValue("user", user?.id);
      setValue("telephone", user?.telephone);
      setValue("adresse", user?.adresse);
      setValue(
        "nom_complet",
        `${user?.prenom || ""} ${user?.nom || ""}`.trim()
      );
      setValue("email", user?.email);
    }
  }, [user]);

  useEffect(() => {
    const produits = cartItems?.map((x) => ({
      produit: x?.produit?.id || x?.produit,
      prix: x?.prix,
      quantite: x?.quantite,
      variations: x?.variations?.map((v) => v.id),
      itemType: x?.itemType ?? "produit",
    }));
    setValue("vendeur", cartItems?.[0]?.vendeur as any);
    setValue("produits", produits as any);
    setValue("montant_total", total);
  }, [cartItems, total]);

  const onSubmitValid = async (data: ServiceFormData) => {
    if (data.consentement !== true) {
      Swal.fire({
        icon: "warning",
        title: "Consentement requis",
        text: "Vous devez accepter le traitement de vos données pour confirmer.",
        iconColor: Color.themeColor,
        confirmButtonColor: Color.themeColor,
      });
      return;
    }

    setSubmitLoading(true);
    try {
      const orderPayload = buildOrderPayloadFromService(data, {
        env: AppBaseUrl.slice(0, -1),
        page: "rendez-vous",
        canale_acquisition: "Site web",
        // Pas d'étape paiement dans ce parcours : règlement sur place / suivi vendeur
        mode_de_paiement: "CASH",
        code_commande: String(Date.now()),
      });

      const res: any = await addOrUpdateOrder({ data: orderPayload });

      if ("data" in res) {
        dispatch(removeCart());
        Swal.fire({
          icon: "success",
          title: "Merci pour votre confiance",
          text: "Votre demande de rendez-vous a bien été prise en compte. Nous vous recontacterons.",
          iconColor: Color.success,
          showConfirmButton: false,
          timer: 9000,
        }).then(() => {
          navigate("/", { replace: true });
        });
        return;
      }

      if ("error" in res) {
        const errMsg =
          res?.error?.data?.message ||
          res?.error?.data?.detail ||
          (typeof res?.error?.data === "string" ? res.error.data : null) ||
          "Une erreur est survenue lors de l'enregistrement";
        Swal.fire({
          icon: "error",
          title: errMsg,
          iconColor: Color.danger,
          showConfirmButton: false,
          timer: 5000,
        });
      }
    } catch (e: any) {
      if (e?.message === "CONSENT_REQUIRED") {
        Swal.fire({
          icon: "warning",
          title: "Consentement requis",
          text: "Vous devez accepter le traitement de vos données.",
          iconColor: Color.themeColor,
        });
      } else {
        Swal.fire({
          icon: "error",
          title: "Une erreur est survenue",
          iconColor: Color.danger,
          showConfirmButton: false,
          timer: 5000,
        });
      }
    } finally {
      setSubmitLoading(false);
    }
  };

  const onSubmit = async (data: ServiceFormData) => {
    if (data?.step && data.step < 4) {
      setValue("step", data.step + 1);
      next();
      return;
    }

    const rawPhone = getInTechTel(data?.telephone || "") || data?.telephone || "";
    const phoneLabel = String(rawPhone)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");

    Swal.fire({
      icon: "question",
      title: "Confirmer votre rendez-vous ?",
      html: `
        <p>Veuillez vérifier vos informations avant de valider.</p>
        <p>Téléphone : <strong>${phoneLabel}</strong></p>
        <p style="font-size:12px;color:#666;margin-top:12px;">
          Vos informations médicales seront transmises de façon confidentielle au praticien uniquement.
        </p>
      `,
      iconColor: Color.themeColor,
      showConfirmButton: true,
      showDenyButton: true,
      confirmButtonText: "Oui, confirmer",
      denyButtonText: "Non, revenir en arrière",
      confirmButtonColor: Color.themeColor,
      denyButtonColor: "#aaa",
    }).then((result) => {
      if (result.isConfirmed) {
        onSubmitValid(data);
      } else if (result.isDenied) {
        setValue("step", 3);
        previous();
      }
    });
  };

  const precedent = () => {
    setValue("step", Math.max(1, (watch("step") || 1) - 1));
    previous();
  };

  return {
    register,
    errors,
    onSubmit: handleSubmit(onSubmit),
    setValue,
    watch,
    isLoading: isLoading || submitLoading,
    Controller,
    control,
    handleChangePhone,
    onChangeAdresse,
    adresse,
    phone,
    precedent,
  };
}

export default UseAddOrUpdateServiceForm;
